import { useState, useRef, useEffect, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Flatpickr from 'react-flatpickr';
import Box from '@mui/material/Box';
import moment from 'moment';
import api_core from '../../../services/config.js';
import PropTypes from "prop-types";
import { esES } from '@mui/x-data-grid/locales';
import { Button } from '@mui/material';
import SoftTypography from 'components/SoftTypography/index.js';
import Operacion from 'Repository/Operacion.js';

moment.locale('es-MX');

const ListadoCreditos = ({ rfc, dates }) => {

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });

    const mapPageToNextCursor = useRef({});
    const [hasNextPage, setHasNextPage] = useState(true);

    const rowCount = useRef(0);
    const isCountRows = useRef(true);

    const operacion = new Operacion();

    const fetchData = useCallback(async (page, pageSize) => {
        setLoading(true);
        try {
            let url = `panel/creditos/consultar?rfc=${rfc}&limit=${pageSize}`;

            if (page > 0 && mapPageToNextCursor.current[page - 1]) {
                url += `&cursor=${encodeURIComponent(mapPageToNextCursor.current[page - 1])}`;
            }

            if (dates.startDate) {
                const formattedStartDate = moment(dates.startDate, 'DD-MM-YYYY').format('YYYY-MM-DD 00:00:00');
                url += `&start_date=${encodeURIComponent(formattedStartDate)}`;
            }

            if (dates.endDate) {
                const formattedEndDate = moment(dates.endDate, 'DD-MM-YYYY').format('YYYY-MM-DD 23:59:59');
                url += `&end_date=${encodeURIComponent(formattedEndDate)}`;
            }

            const isvalid = await operacion.isValidToken();

            const renew = await operacion.refreshToken(isvalid.status);

            const response = await api_core.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('cToken')}`
                }
            });

            const data = await response.data;

            setRows(data.rows);
            setHasNextPage(data.hasNextPage);

            if (data.rows.length > 0 && isCountRows.current) {
                rowCount.current += data.rows.length;
            }

            if (!data.hasNextPage) {
                isCountRows.current = false;
            }

            if (data.cursor) {
                mapPageToNextCursor.current[page] = data.cursor;
            }

        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [rfc, dates, api_core]);


    const prevFilters = useRef({ rfc, dates });

    useEffect(() => {
        const filtersChanged =
            prevFilters.current.rfc !== rfc ||
            prevFilters.current.dates !== dates;

        if (filtersChanged) {

            mapPageToNextCursor.current = {};
            rowCount.current = 0;
            isCountRows.current = true;

            if (paginationModel.page !== 0) {
                setPaginationModel(prev => ({ ...prev, page: 0 }));

                prevFilters.current = { rfc, dates };
                return;
            }

            prevFilters.current = { rfc, dates };
        }

        fetchData(paginationModel.page, paginationModel.pageSize);
    }, [rfc, dates, paginationModel.page, paginationModel.pageSize, fetchData]);



    const handlePageChange = (newPage) => {
        if (newPage > paginationModel.page && !hasNextPage) return;
        setPaginationModel((prev) => ({ ...prev, page: newPage }));
    };

    const columns = [
        { field: 'uuid', headerName: 'uuid' },
        {
            field: 'fecha',
            headerName: 'Fecha',
            headerAlign: 'center',
            align: "center",
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            valueFormatter: (params) => {
            if (!params) return '';
            const date = new Date(params);
            return new Intl.DateTimeFormat('es-MX', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }).format(date);
        },
        },
        {
            field: 'operaciones',
            headerName: 'Operaciones',
            headerAlign: 'center',
            align: "center",
            flex: 1,
            minWidth: 150,
            maxWidth: 400,
        },
        {
            field: 'acumulado',
            headerName: 'Acumulado',
            headerAlign: 'center',
            align: "center",
            flex: 1,
            minWidth: 150,
            maxWidth: 400,
        },
    ];

    return (
        <Box sx={{ height: 700, width: '100%' }}>
            <DataGrid
                cellClassName
                rows={rows}
                columns={columns}
                paginationMode="server"
                sortingMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => handlePageChange(model.page)}
                pageSizeOptions={[15]}
                rowCount={isCountRows.current ? -1 : rowCount.current}
                loading={loading}
                getRowId={(row) => row.uuid}
                columnVisibilityModel={{
                    uuid: false,
                }}
                disableColumnMenu={true}
                disableColumnSorting={true}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                sx={{
                    '& .MuiDataGrid-row:hover': {
                        cursor: 'pointer',
                        color: '#FFFFFF',
                        backgroundColor: '#5F9BC4',
                    },
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                        outline: 'none !important'
                    },
                    '& .MuiDataGrid-row:nth-of-type(even)': {
                        backgroundColor: '#FFFFFF',
                        '&:hover': {
                            backgroundColor: '#5F9BC4'
                        }
                    },
                    '& .MuiDataGrid-row:nth-of-type(odd)': {
                        backgroundColor: '#DCF1FF',
                        '&:hover': {
                            backgroundColor: '#5F9BC4'
                        }
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        borderTopRightRadius: '0.7rem',
                        borderTopLeftRadius: '0.7rem',
                    },
                    '& .MuiDataGrid-columnHeader': {
                        backgroundColor: '#999999',
                        color: '#F2F2F2',
                        margin: '0 0.1rem 0 0',
                        height: '3rem !important',
                        borderTopLeftRadius: '0.7rem',
                        borderTopRightRadius: '0.7rem',
                    },
                    '& .MuiDataGrid-columnHeader:focus': {
                        outline: 'none !important'
                    },
                    '& .MuiCircularProgress-root': {
                        color: '#4A80A8'
                    }
                }}
            />
        </Box>
    );
};

ListadoCreditos.propTypes = {
    rfc: PropTypes.string.isRequired,
    dates: PropTypes.object.isRequired,
};

export default ListadoCreditos;
